import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Link, useNavigate } from 'react-router-dom';
import { usePermissions } from '../context/PermissionsContext';
import { formatDateTime } from '../utils/stringUtils';
import { apiClient } from '../api';
import { DeleteButton } from '../components/shared/DeleteButton';

interface LLMModel {
  name: string;
  value: string;
  cost_per_1k_input: number;
  cost_per_1k_output: number;
}

interface LLMProvider {
  name: string;
  enum_value: string;
  default_rest_endpoint: string;
  icon: string;
  api_key_label: string;
  models: LLMModel[];
}

interface GatewayConfig {
  pipes?: {
    paths?: Array<{ id: string; name: string; prefix: string }>;
    llm?: {
      paths?: Array<{ id: string; name: string; prefix: string }>;
      providers?: LLMProvider[];
    };
    mcp_proxies?: {
      paths?: Array<{ id: string; name: string; prefix: string }>;
    };
  };
}

interface PipeConfig {
  id: string;
  name: string;
  description?: string;
  enabled: boolean;
  pipe_type: {
    type: string;
    provider?: {
      provider_type: string;
      model: string;
      [key: string]: any;
    };
  };
  rate_limit?: {
    enabled?: boolean;
    requests_per_minute?: number;
    tokens_per_minute?: number;
  };
  cost_tracking?: {
    enabled: boolean;
    input_price_per_1k?: number;
    output_price_per_1k?: number;
  };
  override_policy?: Record<string, string>;
  created_at: string;
  updated_at?: string;
}

interface McpProxy {
  id: string;
  name: string;
  description: string;
  channel_prefix: string;
  base_url: string;
  status: string;
  created_at: string;
}

const PipesPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const { hasPermission } = usePermissions();
  const [pipes, setPipes] = useState<PipeConfig[]>([]);
  const [mcpProxies, setMcpProxies] = useState<McpProxy[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadPipes();
    loadMcpProxies();
  }, []);

  const loadPipes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/pipes');
      if (response.ok) {
        const data = await response.json();
        setPipes(data);
      }
    } catch (error) {
      console.error('Failed to load pipes:', error);
    }
    setLoading(false);
  };

  const loadMcpProxies = async () => {
    try {
      const response = await fetch('/api/v1/mcp-proxies');
      if (response.ok) {
        const data = await response.json();
        setMcpProxies(data);
      }
    } catch (error) {
      console.error('Failed to load MCP proxies:', error);
    }
  };



  const handleDelete = async (pipeId: string) => {

    
    try {
      const response = await fetch(`/api/v1/pipes/${pipeId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        loadPipes();
      } else {
        const error = await response.json();
        alert(`Failed to delete pipe: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to delete pipe:', error);
      alert('Failed to delete pipe');
    }
  };

  const handleDeleteMcpProxy = async (proxyId: string) => {
    try {
      const response = await fetch(`/api/v1/mcp-proxies/${proxyId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        loadMcpProxies();
      } else {
        const error = await response.json();
        alert(`Failed to delete MCP Proxy: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to delete MCP proxy:', error);
      alert('Failed to delete MCP Proxy');
    }
  };

  const testPipe = async (pipeId: string) => {
    try {
      const response = await fetch(`/api/v1/pipes/${pipeId}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_message: 'Hello, this is a test' }),
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`Test successful: ${result.message || 'OK'}`);
      } else {
        const error = await response.json();
        alert(`Test failed: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Failed to test pipe:', error);
      alert('Failed to test pipe');
    }
  };

  const editPipe = (pipe: PipeConfig) => {
    navigate(`/pipes/${pipe.id}`);
  };



  const filteredPipes = pipes.filter(pipe => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return true;
    
    const searchLower = trimmedSearch.toLowerCase();
    return (
      pipe.name.toLowerCase().includes(searchLower) ||
      pipe.description?.toLowerCase().includes(searchLower) ||
      pipe.id.toLowerCase().includes(searchLower) ||
      pipe.pipe_type.provider?.provider_type.toLowerCase().includes(searchLower) ||
      pipe.pipe_type.provider?.model.toLowerCase().includes(searchLower)
    );
  });

  const filteredMcpProxies = mcpProxies.filter(proxy => 
    searchTerm === '' || 
    proxy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proxy.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proxy.channel_prefix.toLowerCase().includes(searchTerm.toLowerCase())
  );


  const renderLLMPipesList = () => (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          <i className="fas fa-brain"></i> Language Models
          <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>
            {filteredPipes.length}{searchTerm && ` of ${pipes.length}`}
          </span>
        </h6>
        {hasPermission('pipes.edit') && (
          <button
            className="btn btn-sm btn-primary"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => navigate('/pipes/new')}
          >
            <i className="fas fa-plus"></i> Add LLM Pipe
          </button>
        )}
      </div>
      <div className="card-body">
        {filteredPipes.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-brain fa-3x mb-3" style={{ opacity: 0.3 }}></i>
            <p>{searchTerm ? 'No LLM Pipes match your filter' : 'No LLM Pipes configured yet'}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th style={{width: '15%'}}>Name</th>
                  <th style={{width: '12%'}}>Provider</th>
                  <th style={{width: '12%'}}>Model</th>
                  <th style={{width: '8%'}}>Status</th>
                  <th style={{width: '12%'}}>Rate Limiting</th>
                  <th style={{width: '10%'}}>Cost Tracking</th>
                  <th style={{width: '10%'}}>SDK Override</th>
                  <th style={{width: '10%'}}>Created</th>
                  <th style={{width: '11%', textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPipes.map((pipe) => (
                  <tr 
                    key={pipe.id}
                    onClick={() => editPipe(pipe)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <strong>{pipe.name}</strong>
                      {pipe.description && (
                        <small className="d-block text-muted">{pipe.description}</small>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const providerType = pipe.pipe_type.provider?.provider_type || 'unknown';
                        const providerNames: Record<string, string> = {
                          'openai': 'OpenAI',
                          'anthropic': 'Anthropic',
                          'bedrock': 'AWS Bedrock',
                          'azure_openai': 'Azure OpenAI',
                          'gemini': 'Google Gemini'
                        };
                        return providerNames[providerType] || providerType;
                      })()}
                    </td>
                    <td>
                      <code style={{ fontSize: '0.85rem' }}>
                        {pipe.pipe_type.provider?.model || 'Unknown'}
                      </code>
                    </td>
                    <td>
                      {pipe.enabled ? (
                        <span className="badge badge-success">Enabled</span>
                      ) : (
                        <span className="badge badge-secondary">Disabled</span>
                      )}
                    </td>
                    <td>
                      {pipe.rate_limit && (pipe.rate_limit.requests_per_minute || pipe.rate_limit.tokens_per_minute) ? (
                        <small className="text-success">
                          <i className="fas fa-check-circle"></i> Enabled
                        </small>
                      ) : (
                        <span className="text-muted">Disabled</span>
                      )}
                    </td>
                    <td>
                      {pipe.cost_tracking?.enabled ? (
                        <small className="text-success">
                          <i className="fas fa-check-circle"></i> Enabled
                        </small>
                      ) : (
                        <span className="text-muted">Disabled</span>
                      )}
                    </td>
                    <td>
                      {pipe.override_policy && Object.keys(pipe.override_policy).length > 0 ? (
                        <span 
                          className="badge badge-info" 
                          title={`${Object.keys(pipe.override_policy).length} parameter${Object.keys(pipe.override_policy).length > 1 ? 's' : ''} configured`}
                        >
                          <i className="fas fa-sliders-h"></i> {Object.keys(pipe.override_policy).length}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <small>{pipe.created_at ? formatDateTime(pipe.created_at, true) : '-'}</small>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <button
                        className="btn btn-sm btn-primary mr-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          editPipe(pipe);
                        }}
                        title="Edit pipe"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <DeleteButton
                        onDelete={() => handleDelete(pipe.id)}
                        className="btn-sm"
                        title="Delete pipe"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderMcpProxiesList = () => (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          <i className="fas fa-plug"></i> MCP Proxies
          <span className="badge badge-primary ml-2" style={{ verticalAlign: 'middle' }}>
            {filteredMcpProxies.length}{searchTerm && ` of ${mcpProxies.length}`}
          </span>
        </h6>
        {hasPermission('mcp_proxies.edit') && (
          <button 
            className="btn btn-sm btn-primary"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
            onClick={() => navigate('/pipes/mcp-proxies/wizard')}
          >
            <i className="fas fa-plus"></i> Add MCP Proxy
          </button>
        )}
      </div>
      <div className="card-body">
        {filteredMcpProxies.length === 0 ? (
          <div className="text-center text-muted py-4">
            <i className="fas fa-plug fa-3x mb-3" style={{ opacity: 0.3 }}></i>
            <p>{searchTerm ? 'No MCP Proxies match your filter' : 'No MCP Proxies configured yet'}</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover table-sm">
              <thead>
                <tr>
                  <th style={{width: '20%'}}>Name</th>
                  <th style={{width: '25%'}}>Description</th>
                  <th style={{width: '15%'}}>Channel Prefix</th>
                  <th style={{width: '10%'}}>Status</th>
                  <th style={{width: '12%'}}>Created</th>
                  <th style={{width: '18%', textAlign: 'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMcpProxies.map((proxy) => (
                  <tr 
                    key={proxy.id}
                    onClick={() => navigate(`/pipes/mcp-proxies/${proxy.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><strong>{proxy.name}</strong></td>
                    <td>{proxy.description}</td>
                    <td><code>{proxy.channel_prefix}</code></td>
                    <td>
                      <span className={`badge badge-${proxy.status === 'active' ? 'success' : 'secondary'}`}>
                        {proxy.status.toUpperCase()}
                      </span>
                    </td> 
                    <td><small>{formatDateTime(proxy.created_at, true)}</small></td>
                    <td style={{textAlign: 'right'}}>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/pipes/mcp-proxies/${proxy.id}`);
                        }}
                        title="Edit"
                        style={{marginRight: '10px'}}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <DeleteButton
                        onDelete={() => handleDeleteMcpProxy(proxy.id)}
                        className="btn-sm"
                        title="Delete MCP Proxy (will also remove all associated channels)"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => navigate('/')}
          >
            <i className="fas fa-times"></i> Close
          </button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.75rem' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '0.875rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control"
              placeholder="Filter Pipes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '240px', paddingLeft: '2.25rem', backgroundColor: 'rgba(255, 255, 255, 0.3)', border: 'none', fontSize: '0.875rem', outline: 'none', boxShadow: 'none' }}
              onFocus={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
        </div>
        <div>
          <Link 
            to="/pipes/metrics" 
            className="btn btn-sm btn-info"
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            <i className="fas fa-chart-line"></i> View Metrics
          </Link>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <div className="spinner-border" role="status" style={{ color: 'rgba(0, 0, 0, 0.5)' }}>
            <span className="visually-hidden"></span>
          </div>
        </div>
      ) : (
        <>
          {renderLLMPipesList()}
          {renderMcpProxiesList()}
        </>
      )}


    </div>
  );
};

export default PipesPage;
